/**
 * The background work the phone runs from its root layout, for the browser.
 *
 * Each one is `apps/mobile/src/app/_layout.tsx`'s own manager with one change:
 * the phone listens to AppState, and a browser tab listens to visibility. A
 * hidden tab is a backgrounded app.
 */
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { useActiveTenant } from '@/hooks/use-active-tenant';
import { useMe } from '@/hooks/use-me';
import { runFieldIndexBackfill } from '@/lib/field-index-backfill';
import { runSearchBackfill } from '@/lib/search-backfill';
import { startEvents, stopEvents } from '@/lib/sync-events';
import { sync } from '@/lib/sync-engine';
import { shouldRunScheduledSync, SYNC_INTERVAL_MS } from '@/lib/sync-failure';
import { useSetupDraftStore } from '@/stores/setup-draft-store';
import { useSyncStore } from '@/stores/sync-store';

const isVisible = (): boolean => document.visibilityState === 'visible';

/** Runs `work` each time the tab comes back into view. */
const onVisible = (work: () => void): (() => void) => {
  const listener = (): void => {
    if (isVisible()) work();
  };
  document.addEventListener('visibilitychange', listener);
  return () => document.removeEventListener('visibilitychange', listener);
};

/** Pull on open, on return, on the socket's ping, and every 45 seconds while visible. */
export function SyncManager(): null {
  const { tenantId } = useActiveTenant();

  useEffect(() => {
    if (tenantId === null) return;
    startEvents(tenantId);
    return () => stopEvents();
  }, [tenantId]);

  useEffect(() => {
    if (tenantId === null) return;
    void sync(tenantId);
    const stop = onVisible(() => void sync(tenantId));
    const interval = setInterval(() => {
      if (!isVisible()) return;
      if (!shouldRunScheduledSync(useSyncStore.getState().lastFailure, Date.now())) return;
      void sync(tenantId);
    }, SYNC_INTERVAL_MS);
    return () => {
      stop();
      clearInterval(interval);
    };
  }, [tenantId]);

  return null;
}

/** Builds the search index and the field indexes for whatever the mirror holds. */
export function SearchIndexManager(): null {
  const { state } = useMe();
  const ready = state.status === 'ready';
  useEffect(() => {
    if (!ready) return;
    void runSearchBackfill();
    void runFieldIndexBackfill();
    return onVisible(() => void runSearchBackfill());
  }, [ready]);
  return null;
}

/**
 * After setup makes a business, switch to it and land on its first page:
 * the Dashboard for a person who may see it, the records otherwise.
 */
export function SetupLanding(): null {
  const { businesses, switchTo } = useActiveTenant();
  const createdId = useSetupDraftStore((store) => store.createdId);
  const landing = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (createdId === null || landing.current === createdId) return;
    const membership = businesses.find((one) => one.tenantId === createdId);
    if (membership === undefined) return;
    landing.current = createdId;
    const first = membership.capabilities.includes('view_business_insights') ? '/dashboard' : '/';
    void switchTo(createdId).then(() => {
      useSetupDraftStore.getState().landed();
      void navigate({ to: first, replace: true });
    });
  }, [createdId, businesses, switchTo, navigate]);

  return null;
}
